import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  DocumentTextIcon,
  BriefcaseIcon,
  ScaleIcon,
  UsersIcon,
  ArrowRightIcon
} from "@heroicons/react/24/solid";
import BackGround from "../../assets/inter-allie.jpg";

const categories = [
  {
    id: "marketing",
    title: "Marketing",
    description: "Boostez votre communication et votre présence avec nos ressources marketing.",
    image: "https://declic-marketing.ch/wp-content/uploads/2017/10/Website_ArticleMKT2.jpg",
    color: "text-blue-600",
    icon: <BriefcaseIcon className="w-10 h-10 text-blue-500" />
  },
  {
    id: "legal",
    title: "Légal",
    description: "Accédez aux documents légaux pour sécuriser vos activités et relations professionnelles.",
    image: "https://www.totallylegal.com/getasset/56176023-541f-4fb9-94f4-08772a06096f/",
    color: "text-red-600",
    icon: <ScaleIcon className="w-10 h-10 text-red-500" />
  },
  {
    id: "client",
    title: "Client",
    description: "Trouvez tous les supports nécessaires pour améliorer l’expérience client.",
    image: "https://fundcount-com-assets.s3.us-east-2.amazonaws.com/wp-content/uploads/2023/11/1280x600_1_1-1.png",
    color: "text-green-600",
    icon: <UsersIcon className="w-10 h-10 text-green-500" />
  }
];

export default function Documentation() {
  const [documents, setDocuments] = useState({ marketing: [], legal: [], client: [] });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const BACKEND_URL =
  window.location.hostname === "localhost"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PROD;

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ressources`);
      const categorizedDocs = { marketing: [], legal: [], client: [] };

      res.data.forEach((doc) => {
        const cat = doc.categorie?.toLowerCase();
        if (cat === "marketing") categorizedDocs.marketing.push(doc);
        else if (cat === "legal") categorizedDocs.legal.push(doc);
        else if (cat === "client") categorizedDocs.client.push(doc);
      });

      setDocuments(categorizedDocs);
    } catch (err) {
      console.error("❌ Erreur lors de la récupération des documents :", err);
    }
  };

  return (
    <div
      className="relative h-[92vh] py-20 px-8 lg:px-24 bg-cover bg-center shadow-lg overflow-hidden"
      style={{ backgroundImage: `url(${BackGround})` }}
    >
      {/* Overlay sombre et flou */}
      <div className="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-lg"></div>

      {/* Header */}
      <div className="relative z-10 flex items-center justify-center space-x-4 -mt-12">
        <motion.div 
          className="flex items-center justify-center p-3 bg-white shadow-md rounded-full"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <DocumentTextIcon className="w-10 h-10 text-blue-500" />
        </motion.div>

        <h1 className="text-4xl sm:text-5xl font-extrabold text-white">
          Ressources Documentaires
        </h1>
      </div>

      {/* Cartes de catégories */}
      <div className="relative z-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 mt-16">
        {categories.map((category, index) => (
          <motion.div 
            key={category.id}
            className="group rounded-3xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
            whileHover={{ scale: 1.03 }}
          >
            {/* Image */}
            <div className="overflow-hidden rounded-t-3xl">
              <img 
                src={category.image} 
                alt={category.title} 
                className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>

            {/* Contenu */}
            <div className="p-8 flex flex-col items-center text-center">
              <div className="mb-4">{category.icon}</div>
              <h3 className={`text-2xl font-semibold ${category.color} mb-4`}>{category.title}</h3>
              <p className="text-gray-600 text-base leading-relaxed">{category.description}</p>
            </div>

            {/* Liste des documents ou bouton "Consulter" */}
            <div className="p-8 pt-2">
              <button
                onClick={() => navigate(`/documents/${category.id}`)}
                className="flex items-center justify-center w-full mt-2 px-6 py-3 text-white text-lg bg-blue-500 hover:bg-blue-600 rounded-xl transition-all"
              >
                <span>Consulter</span>
                <ArrowRightIcon className="w-6 h-6 ml-3" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
